import React, { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HeimdallContext from "../HeimdallContext";
import { UserData } from "../context/auth";
import { faClock, faCogs, faCubes, faDatabase, faEnvelope, faSignIn, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Box, Fade, List, ListItemButton, Typography, useTheme } from "@mui/material";
import { FormattedMessage } from "react-intl";

const arrowSize = 10;
const itemPadding = 16;

const SectionTitle = ({ label }: { label: string }) => (
    <Typography sx={{ textTransform: "uppercase" }}>
        <FormattedMessage id={label} />
    </Typography>
);

type SidebarProps = {
    context: HeimdallContext;
    userData: UserData;
};

const SidebarItem = ({
    label,
    path,
    icon,
    iconColor,
}: {
    label: string;
    path: string;
    icon: IconDefinition;
    iconColor: string;
}) => {
    const location = useLocation();
    const theme = useTheme();
    const active = location.pathname.startsWith(path);
    return (
        <ListItemButton
            selected={active}
            component={Link}
            to={{ pathname: path }}
            style={{
                paddingLeft: 0,
                paddingRight: itemPadding,
            }}
        >
            <Box style={{ display: "flex", width: "100%", alignItems: "center" }}>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                        <FontAwesomeIcon icon={icon} color={iconColor} />
                    </Box>
                    <Box sx={{ marginLeft: 2 }}>
                        <FormattedMessage id={label} />
                    </Box>
                </Box>
                <Fade in={active}>
                    <div
                        style={{
                            borderTop: `${arrowSize}px solid transparent`,
                            borderBottom: `${arrowSize}px solid transparent`,
                            borderRight: `${arrowSize}px solid ${theme.palette.background.default}`,
                            height: arrowSize,
                            marginRight: -itemPadding,
                        }}
                    />
                </Fade>
            </Box>
        </ListItemButton>
    );
};

export const Sidebar = ({ context, userData }: SidebarProps) => {
    let sidebarItems: ReactNode[] = [];
    let userListUrl = context.isInternalIdenityProvider ? "users" : "user_subs";

    if (context.isInternalIdenityProvider) {
        sidebarItems.push(<SectionTitle key="sidebar_menu.account" label="sidebar_menu.account" />);
        if (userData.userId)
            sidebarItems.push(
                <SidebarItem
                    key="sidebar_menu.general"
                    label="sidebar_menu.general"
                    path="/account/general"
                    icon={faCogs}
                    iconColor="cyan"
                />,
                <SidebarItem
                    key="sidebar_menu.emails"
                    label="sidebar_menu.emails"
                    path="/account/emails"
                    icon={faEnvelope}
                    iconColor="cyan"
                />,
                <SidebarItem
                    key="sidebar_menu.aliases"
                    label="sidebar_menu.aliases"
                    path="/account/aliases"
                    icon={faSignIn}
                    iconColor="cyan"
                />,
            );
        sidebarItems.push(
            <SidebarItem
                key="sidebar_menu.sessions"
                label="sidebar_menu.sessions"
                path="/account/sessions"
                icon={faClock}
                iconColor="cyan"
            />,
        );
    }

    if (userData.hasPermission("system")) {
        sidebarItems.push(
            <SectionTitle key={"sidebar_menu.admin"} label="sidebar_menu.admin" />,
            <SidebarItem
                key="sidebar_menu.clients"
                label="sidebar_menu.clients"
                path="/clients/list"
                icon={faCubes}
                iconColor="lime"
            />,
            <SidebarItem
                key="sidebar_menu.users"
                label="sidebar_menu.users"
                path={`/${userListUrl}/list`}
                icon={faUsers}
                iconColor="lime"
            />,
        );

        if (global.identityProvider === "internal" && window.features.ldap)
            sidebarItems.push(
                <SidebarItem
                    key="sidebar_menu.ldap"
                    label="sidebar_menu.ldap"
                    path="/ldap_auths/list"
                    icon={faDatabase}
                    iconColor="lime"
                />,
            );
    }

    return <List sx={{ paddingLeft: 2 }}>{...sidebarItems}</List>;
};
